import { useEffect, useMemo, useRef, useState } from 'react'
import Btn from '../../../../UI/Btn'
import Table from '../../../../UI/Table'
import MiniHeader from '../../../../UI/MiniHeader'
import { useHistory } from 'react-router-dom'
import http from '../../../../shared/http'
import { TApi, TCompanies, TRoles, TRoutes } from '../../../../shared/const'
import styles from './style.module.scss'
import BtnTooltip from '../../../../UI/BtnTooltip'
import Search from '../../../../UI/Search'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	branchField,
	companyStatusField,
	companyTypeField,
	createdWithinField,
	schoolField,
	schoolTypeField,
	stateField,
	teacherField
} from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import { useTranslation } from 'react-i18next'
import Pagination from '../../../../UI/Pagination'
import {
	blobTypes,
	customNumberFormat,
	downloadFile,
	exportFileName,
	personName,
	stateFullLabel,
	stateLabel
} from '../../../../shared/helpers'
import clsx from 'clsx'
import Icon from '../../../../UI/Icon'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import { connect } from 'react-redux'
import { setSearchLimit } from '../../../../redux/ducks/user'
import LimitFilterButton from '../../../../UI/LimitFilterButton'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import { useIsReadOnly } from '../../../../shared/alumni'
import TypeTiles from './TypeTiles'

const saldo = {
	JC: '800',
	JCC: '800',
	JBC: '300'
}

function Companies(props) {
	const { t, i18n } = useTranslation()
	const history = useHistory()
	const [companies, setCompanies] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'createdAt', direction: 'desc' })
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	const [loading, setLoading] = useState(false)
	// Alumni (#213): nur lesen — kein Löschen, kein Export, keine Liste zum Herunterladen.
	const readOnly = useIsReadOnly()

	// Bundesland-Admins sehen ohnehin nur ihr eigenes Bundesland (das setzt das
	// Backend), ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			// Mit der Junior Mini Company als vierter Form (#92).
			companyTypeField(t, { withJmc: !readOnly }),
			companyStatusField(t),
			branchField(t, props.branch, i18n.language),
			schoolTypeField(t, props.schoolTypes),
			schoolField(t),
			teacherField(t),
			// Ziel der Kachel „Neu" auf der Startseite (shared/widgetListLink).
			createdWithinField(t)
		],
		[t, i18n.language, props.role, props.branch, props.schoolTypes, readOnly]
	)

	const filters = useFilters({ storageKey: 'companies', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)

	const options = [
		{ value: 10, label: 10 },
		{ value: 100, label: 100 },
		{ value: 1000, label: 1000 }
	]
	const { defaultLimit } = props
	const handleChangeLimit = selectedOption => {
		props.setSearchLimit(selectedOption.value)
	}

	// тут запит на сервер зі всіми фільтрами (з пагінацією і з пошуком також)
	const offset = page * defaultLimit

	// Eine Antwort, die nach dem Verlassen der Seite eintrifft, darf nicht mehr
	// in den Zustand schreiben (React meldet das sonst als Warnung).
	const mounted = useRef(true)
	useEffect(() => () => (mounted.current = false), [])

	// Zahlen der vier Kacheln (#92) — unabhängig von Filter und Seite.
	const [typeCounts, setTypeCounts] = useState(null)
	const fetchTypeCounts = () =>
		http.get(TApi.ADMIN_COMPANIES_TYPE_COUNTS, { disableToast: true })
			.then(({ data }) => mounted.current && setTypeCounts(data.counts))
			.catch(() => mounted.current && setTypeCounts(null))
	useEffect(() => {
		fetchTypeCounts()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const fetchAllCompanies = () => {
		http.get(TApi.ADMIN_GET_COMPANIES, {
			params: {
				limit: defaultLimit,
				offset,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!mounted.current) return

			const tableArray = data.allCompanies.map(v => {
				// Junior Mini Company (#92): eigene Tabellen, keine Berichte und
				// keine Rechnungen. Statt Mitarbeiter:innen zählen die Kinder,
				// das Einverständnis ist eine einzige PDF-Datei.
				if (v.kind === 'JMC')
					return {
						...v,
						balance: '—',
						reportsCount: '—',
						school: v.school?.name || '-',
						employeesCount: v.participantCount,
						supervisionTeacher: (v.teachers || []).join(', ') || '-'
					}
				const supervision = v.TeachersSupervisions.find(({ isSupervision }) => isSupervision)
				const supervisionTeacher = personName(supervision?.Teacher) || '-'
				// „Saldo" ist der offene Betrag der Rechnungen einer Firma. Bis
				// 07.08.2026 standen hier zwei Werte nebeneinander („- | -"): die
				// Salden der zwei moeglichen Rechnungen (Halbjahres- und
				// Jahresbericht), jede fehlende als Strich. Bezahlt (Saldo 0) fiel
				// dabei ebenfalls auf „-", weil 0 als „nicht vorhanden" durchging.
				// Jetzt steht dort eine Zahl: die Summe der offenen Betraege. Der
				// Strich bleibt nur, wenn es noch gar keine Rechnung gibt.
				const invoices = v.Invoice || []
				return {
					...v,
					balance: invoices.length
						? customNumberFormat(invoices.reduce((sum, invoice) => sum + (invoice.balance || 0), 0))
						: '-',
					school: v.TeachersSupervisions?.[0]?.Teacher?.School?.name || '-',
					state: v.state,
					reportsCount:
						TCompanies.JC === v.companyType
							? (v.AnnualReport?.isSubmitted && v.SemiannualReport?.isSubmitted && '2') ||
							  (v.AnnualReport?.isSubmitted && 1) ||
							  (v.SemiannualReport?.isSubmitted && 1) ||
							  '0'
							: (v.SemiannualReport?.isSubmitted && 1) || '0',
					saldo: saldo[v.companyType],
					supervisionTeacher
				}
			})
			setCompanies(tableArray)
			setCountAll(data.count)
		})
	}

	// Gefiltert und gesucht wird ohne Knopfdruck: jede Auswahl laedt die Liste
	// neu. Eine Aenderung an Suche oder Filter setzt die Seite zurueck — sonst
	// stuende man auf Seite 4 einer Liste, die nur noch zwei Seiten hat.
	useEffect(() => {
		fetchAllCompanies()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, defaultLimit, debouncedSearch, filterQuery])

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	const handleOpen = (id, kind) =>
		history.push(kind === 'JMC' ? TRoutes.ADMIN_MINI_COMPANY(id) : TRoutes.COMPANY_REVIEW(id))

	// Kachel geklickt: Unternehmensform (und Stufe) als Filter setzen; ein
	// zweiter Klick auf dieselbe Auswahl hebt sie wieder auf.
	const selectedTypes = (filters.values.companyType || []).map(o => o.value)
	const selectedStatuses = (filters.values.companyStatus || []).map(o => o.value)
	const handleTileSelect = (typ, status) => {
		const schonGewaehlt =
			selectedTypes.length === 1 &&
			selectedTypes[0] === typ &&
			(status ? selectedStatuses.length === 1 && selectedStatuses[0] === status : !selectedStatuses.length)
		const typFeld = fields.find(f => f.key === 'companyType')
		const statusFeld = fields.find(f => f.key === 'companyStatus')
		setPage(0)
		filters.setField('companyType', schonGewaehlt ? [] : typFeld.options.filter(o => o.value === typ))
		filters.setField('companyStatus', schonGewaehlt || !status ? [] : statusFeld.options.filter(o => o.value === status))
	}

	const openConfirmRemoveModal = async (id, name) => {
		const { isConfirmed } = await Swal.fire({
			title: t('Are you sure?'),
			html: `<b>„${name}"</b><br/><br/>${t('You will not be able to revert this!')}<br/><br/>${t('Type {{word}} to confirm', { word: t('Delete') })}`,
			icon: 'warning',
			input: 'text',
			inputPlaceholder: t('Delete'),
			inputAttributes: { autocapitalize: 'off', autocorrect: 'off' },
			inputValidator: value =>
				(value || '').trim().toLowerCase() !== t('Delete').toLowerCase() ? t('Input does not match') : undefined,
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#6c757d',
			confirmButtonText: t('Delete'),
			cancelButtonText: t('Cancel')
		})

		if (!isConfirmed) return

		setLoading(true)
		try {
			await http.delete(TApi.ADMIN_DELETE_COMPANY(id))
			fetchAllCompanies()
			fetchTypeCounts()
			toast.success(t('The company successfully deleted'))
		} finally {
			setLoading(false)
		}
	}

	const downloadExport = (id, name) => {
		setLoading(true)
		http.get(TApi.ADMIN_DOWNLOAD_COMPANY_DATA(id), {
			responseType: 'arraybuffer'
		})
			.then(res => {
				var blob = new Blob([res.data], {
					type: blobTypes.XLS
				})
				downloadFile(exportFileName(name), blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const columns = useMemo(
		() => [
			{
				Header: t('Company'),
				accessor: 'name',
				Cell: ({ row }) => (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							wordBreak: 'break-word',
							width: '100%',
							justifyContent: 'space-between'
						}}
					>
						{/* Geöffnet wird über die ganze Zeile (onRowClick), nicht nur
						    über den Namen — Farbe und Schnitt kommen aus UI/Table.
						    Der Name nimmt den Platz bis zum Menü (`flex: 1`, keine feste
						    Breite): Bei 120 px brach fast jeder Firmenname um. */}
						<span style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>{row.values.name}</span>
						{/* Alumni (#213): kein Löschen, kein Export — das Öffnen
						    erledigt die Zeile, ein Menü mit nur diesem Eintrag
						    wäre überflüssig. */}
						{!readOnly && row.original.kind === 'JMC' && (
							<div style={{ marginRight: '20px' }}>
								<BtnTooltip
									items={[{ label: t('Open profile'), onClick: () => handleOpen(row.original.id, 'JMC') }]}
								/>
							</div>
						)}
						{!readOnly && row.original.kind !== 'JMC' && (
							<div style={{ marginRight: '20px' }}>
								<BtnTooltip
									items={[
										{
											label: t('Open profile'),
											onClick: () => handleOpen(row.original.id)
										},
										{
											disabled:
												row.original.isActive ||
												row.original.SemiannualReport ||
												row.original.AnnualReport,
											label: t('Delete'),
											onClick: () => openConfirmRemoveModal(row.original.id, row.original.name)
										},
										{
											label: t('Export'),
											onClick: () => downloadExport(row.original.id, row.original.name)
										}
									]}
								/>
							</div>
						)}
					</div>
				),
				// Die breiteste Spalte der Liste: Firmennamen sind lang ("ZZTEST JBC
				// Textilkreis") und sollen in einer Zeile stehen.
				width: 260,
				minWidth: 200
			},

			{
				Header: t('Type'),
				accessor: 'companyType',
				align: 'center',
				width: 60
			},
			{
				// „Unternehmensstatus" hiess die Spalte bis 07.08.2026 — in einer
				// Unternehmensliste ist das doppelt gesagt.
				Header: t('Status'),
				accessor: 'isActive',
				// Bis 07.08.2026 stand hier ein einzelner Buchstabe (C/A/R) ohne
				// Legende — der Status steht jetzt ausgeschrieben da (#79).
				Cell: ({ row }) => {
					if (row.original.isNeedReview) return <span className={styles.red}>{t('To be checked')}</span>
					if (row.original.isActive) return <span className={styles.green}>{t('Active')}</span>
					return t('Registered')
				},
				width: 95
			},
			{
				// Mitarbeiter:innen und Einverstaendnis der Eltern gehoeren
				// zusammen: Interessant ist nicht jede Zahl fuer sich, sondern ob
				// beide gleich sind (4 / 4 = alle Einverstaendnisse liegen vor).
				// Sortiert wird nach der Anzahl Mitarbeiter:innen, das kann das
				// Backend; das Einverstaendnis zaehlt es nicht.
				Header: t('Emp. / consent'),
				headerToolTip: t('Employees and parental consent'),
				accessor: 'employeesCount',
				align: 'center',
				Cell: ({ row }) => {
					// JMC: Kinder / ob die eine PDF mit allen Erklärungen vorliegt.
					if (row.original.kind === 'JMC')
						return (
							<span title={t('Children and consent forms (one PDF)')}>
								{row.original.participantCount} /{' '}
								<span className={row.original.hasConsent ? styles.green : styles.red}>PDF</span>
							</span>
						)
					const employees = row.original.employeesCount ?? 0
					const consent = row.original.parentalConfirmationCount ?? 0
					return (
						<span>
							{employees} /{' '}
							<span className={employees === consent ? styles.green : styles.red}>{consent}</span>
						</span>
					)
				},
				width: 100
			},
			{
				Header: t('School'),
				accessor: 'school',
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>{row.values.school}</span>
				),
				width: 150
			},
			{
				// Bundeslaender stehen ueberall in der Kurzform (stateLabel), der
				// volle Name kommt beim Darueberfahren.
				Header: t('Fed. State'),
				accessor: 'state',
				cellToolTip: ({ row }) => stateFullLabel(row.original.state),
				Cell: ({ row }) => stateLabel(row.original.state),
				width: 70
			},
			{
				Header: t('Reports'),
				accessor: 'reportsCount',
				disableSortBy: true,
				align: 'center',
				width: 70
			},

			{
				Header: t('Balance'),
				accessor: 'balance',
				align: 'center',
				width: 80
			},
			{
				Header: t('Teacher'),
				accessor: 'supervisionTeacher',
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>{row.values.supervisionTeacher}</span>
				),
				minWidth: 120
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, readOnly]
	)

	const downoloadList = fileType => {
		setLoading(true)

		http.get(TApi.ADMIN_DOWNOLOAD_COMPANIES_LIST, {
			params: {
				type: fileType,
				leng: localStorage.getItem('i18nextLng')
			},
			responseType: 'arraybuffer'
		})
			.then(res => {
				var blob = new Blob([res.data], {
					type: blobTypes[fileType]
				})
				downloadFile('Companies', blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('companies')}>
				<div className={styles.miniHeaderContent}>
					<Search
						value={search}
						placeholder={t('Search companies...')}
						onChange={e => handleSearchChange(e.target.value)}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter
						icon="arrow"
						onClick={() => setShowFilter(true)}
						count={filters.count}
					>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>
			<TypeTiles
				counts={typeCounts}
				selectedTypes={selectedTypes}
				selectedStatuses={selectedStatuses}
				onSelect={handleTileSelect}
			/>
			<div className={styles.subHeader}>
				<Pagination
					limit={defaultLimit}
					page={page}
					label={t('companies')}
					countAll={countAll}
					onChange={setPage}
				/>
				{!readOnly && (
					<div className={styles.downloadContent}>
						{t('Download list as')}
						<div>
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => downoloadList('XLS')}
								>
									XLS
								</Btn>
							)}
							{loading && (
								<Icon
									className={clsx(styles.icon, styles.rootLoading)}
									size={{ w: 16, h: 16 }}
									name="loading"
									color={'#22404D'}
								/>
							)}
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => downoloadList('PDF')}
								>
									PDF
								</Btn>
							)}
						</div>
					</div>
				)}
				<LimitFilterButton limit={defaultLimit} options={options} onChange={handleChangeLimit} />
			</div>
			<div className="container">
				<Table
					columns={columns}
					items={companies}
					page={page}
					limit={defaultLimit}
					countAll={countAll}
					onChange={setPage}
					handleSort={handleSort}
					onRowClick={company => handleOpen(company.id, company.kind)}
					rowTitle={t('Open profile')}
				/>
			</div>
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}
const mapStateToProps = state => ({
	defaultLimit: state.user.defaultLimit,
	email: state.user.profile.email,
	role: state.user.role,
	branch: state.options.branch,
	schoolTypes: state.options.schoolTypes
})

const mapDispatchToProps = dispatch => ({
	setSearchLimit: data => dispatch(setSearchLimit(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(Companies)
